import { ENTITY_TYPE, API_RESPONSE_TYPE } from '../../constants'
import { getAccountTransactionsApi } from '../Api/StafflinkApi'
import { ROLE } from '../../state/transaction/transaction.types'
import { getTransactionDatabaseManager } from '../IndexDB/TransactionDatabaseManager'
import { AuthStorageInstance } from '../Storage/AuthStorage'
import { Mixpanel } from '../Tracking/Mixpanel'

export interface ISimpleSyncResult {
  success: boolean
  data: any[]
  error?: string
}

export class SimpleSmartSync {
  private dbManager: any = null
  private fileResponseCount = 0

  private trackFileResponseCount(entityId: string, entityType: ENTITY_TYPE): void {
    Mixpanel.track('txn_sync_file_response', {
      account_id: entityId,
      entity_type: entityType === ENTITY_TYPE.CUSTOMER ? 'customer' : 'supplier',
      file_response_count: this.fileResponseCount
    })
  }

  async initialize(): Promise<void> {
    try {
      this.dbManager = getTransactionDatabaseManager()
      await this.dbManager.init()
      console.log('[SimpleSmartSync] Initialized successfully')
    } catch (error) {
      console.error('[SimpleSmartSync] Failed to initialize:', error)
      throw error
    }
  }

  async syncTransactions(
    entityId: string,
    entityType: ENTITY_TYPE,
    orderBy: number = 3
  ): Promise<ISimpleSyncResult> {
    this.fileResponseCount = 0

    try {
      await this.initialize()

      console.log('[SimpleSmartSync] Starting sync for:', { entityId, entityType })

      // Check if we have cached data
      const hasCachedData = await this.dbManager.hasData(entityId, entityType)
      const now = Date.now()
      const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000

      let transactions: any[] = []

      if (hasCachedData) {
        // Default: trailing 1 year. If the entity has been fully seeded, fetch only
        // the delta since the newest SERVER update_time we have stored (Android
        // parity) instead of re-pulling a whole year.
        let startTime = Math.floor(oneYearAgo / 1000)
        try {
          const metadata = await this.dbManager.getMetadata(entityId, entityType)
          if (metadata?.hasCompletedFullFetch) {
            const maxServerUpdate = await this.dbManager.getMaxServerUpdateTime(
              entityId,
              entityType
            )
            if (maxServerUpdate && maxServerUpdate > 0) {
              startTime = maxServerUpdate
              console.log('[SimpleSmartSync] Seeded entity — delta fetch from', startTime)
            }
          }
        } catch (cursorErr) {
          console.warn('[SimpleSmartSync] Could not compute delta cursor, using 1yr:', cursorErr)
        }

        console.log('[SimpleSmartSync] Fetching recent data from start_time', startTime)
        transactions = await this.fetchData(
          entityId,
          entityType,
          {
            start_time: startTime,
            end_time: Math.floor(now / 1000)
          },
          orderBy
        )

        // Merge with existing cached data
        if (transactions.length > 0) {
          const cachedData = await this.dbManager.getTransactionsByEntity(entityId, entityType)
          const cachedMap = new Map(cachedData.map((t: any) => [t.id, t]))
          transactions.forEach((transaction: any) => {
            cachedMap.set(transaction.id, transaction)
          })
          transactions = Array.from(cachedMap.values())
        } else {
          // Use cached data if no new data found
          transactions = await this.dbManager.getTransactionsByEntity(entityId, entityType)
        }
      } else {
        // No cached data, try start_time = 0 first
        console.log('[SimpleSmartSync] No cached data, trying start_time=0...')
        transactions = await this.fetchData(
          entityId,
          entityType,
          {
            start_time: 0,
            end_time: Math.floor(now / 1000)
          },
          orderBy
        )

        // If no data with start_time=0, use smart gaps
        if (transactions.length === 0) {
          console.log('[SimpleSmartSync] No data with start_time=0, trying smart gaps...')
          transactions = await this.fetchDataWithSmartGaps(
            entityId,
            entityType,
            0,
            Math.floor(now / 1000),
            orderBy
          )
        }

        // Mark that we've completed a full fetch
        if (transactions.length > 0) {
          try {
            await this.dbManager.setFullFetchCompleted(entityId, entityType, true)
            console.log('[SimpleSmartSync] Marked full fetch as completed')
          } catch (error) {
            console.warn('[SimpleSmartSync] Could not update full fetch flag:', error)
          }
        } else {
          // No data found at all, mark as completed to avoid future unnecessary calls
          try {
            await this.dbManager.setFullFetchCompleted(entityId, entityType, true)
            console.log('[SimpleSmartSync] No data found, marked full fetch as completed')
          } catch (error) {
            console.warn('[SimpleSmartSync] Could not update full fetch flag:', error)
          }
        }
      }

      // Store data in IndexDB
      if (transactions.length > 0) {
        // This replaces the account's record, so re-tag it with the active business.
        const businessId: string | undefined =
          (await AuthStorageInstance.getCurrentBusinessId()) || undefined
        await this.dbManager.storeTransactions(entityId, entityType, transactions, businessId)
        console.log(
          '[SimpleSmartSync] Data stored successfully:',
          transactions.length,
          'transactions'
        )
      }

      this.trackFileResponseCount(entityId, entityType)

      return {
        success: true,
        data: transactions
      }
    } catch (error: any) {
      console.error('[SimpleSmartSync] Error in sync:', error)
      this.trackFileResponseCount(entityId, entityType)
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  private async fetchData(
    entityId: string,
    entityType: ENTITY_TYPE,
    window: { start_time: number; end_time: number },
    orderBy: number
  ): Promise<any[]> {
    const isCustomer = entityType === ENTITY_TYPE.CUSTOMER

    console.log('[SimpleSmartSync] Fetching data with params:', {
      id: entityId,
      start_time: window.start_time,
      end_time: window.end_time,
      orderBy,
      role: isCustomer ? ROLE.CUSTOMER : ROLE.SUPPLIER
    })

    const response = await getAccountTransactionsApi({
      id: entityId,
      start_time: window.start_time,
      end_time: window.end_time,
      orderBy,
      role: isCustomer ? ROLE.CUSTOMER : ROLE.SUPPLIER,
      is_asc_order: true
    })
    if (response.type === API_RESPONSE_TYPE.SUCCESS && response.data?.file_data) {
      this.fileResponseCount++
    }

    console.log('[SimpleSmartSync] API response:', {
      type: response.type,
      hasData: !!response.data?.list_data?.transactions,
      transactionCount: response.data?.list_data?.transactions?.length || 0,
      listDataNull: response.data?.list_data === null
    })

    // Case 1: Data received successfully
    if (response.type === API_RESPONSE_TYPE.SUCCESS && response.data?.list_data?.transactions) {
      const transactions = response.data.list_data.transactions
      console.log('[SimpleSmartSync] Data received:', transactions.length, 'transactions')
      return transactions
    }

    // Case 2: start_time = 0. A large full history comes back as a FILE response
    // (file_data set, list_data null). Decode it instead of giving up — previously
    // this returned [] whenever list_data was empty, IGNORING file_data, so accounts
    // whose full history is delivered as a file never populated (empty ledger).
    if (response.type === API_RESPONSE_TYPE.SUCCESS && window.start_time === 0) {
      if (response.data?.file_data) {
        const decoded = await this.tryDecodeFileData(response.data.file_data)
        if (decoded && decoded.length > 0) {
          console.log(
            '[SimpleSmartSync] start_time=0 FILE decoded:',
            decoded.length,
            'transactions'
          )
          return decoded
        }
        // Decode unsupported/failed → fall through to smart-gap reduction (Case 3).
      } else if (
        !response.data?.list_data ||
        response.data?.list_data === null ||
        response.data?.list_data?.transactions?.length === 0
      ) {
        console.log('[SimpleSmartSync] start_time=0 returned no data, no data available')
        return []
      }
    }

    // Case 3: list_data is null or empty, use smart gap reduction
    if (
      response.type === API_RESPONSE_TYPE.SUCCESS &&
      (!response.data?.list_data ||
        response.data?.list_data === null ||
        response.data?.list_data?.transactions?.length === 0)
    ) {
      console.log('[SimpleSmartSync] No data available, trying smart gap reduction...')
      return await this.fetchDataWithSmartGaps(
        entityId,
        entityType,
        window.start_time,
        window.end_time,
        orderBy
      )
    }

    console.log('[SimpleSmartSync] No data received from API')
    return []
  }

  private async fetchDataWithSmartGaps(
    entityId: string,
    entityType: ENTITY_TYPE,
    startTime: number,
    endTime: number,
    orderBy: number
  ): Promise<any[]> {
    const gaps = [
      365 * 24 * 60 * 60, // 1 year
      180 * 24 * 60 * 60, // 6 months
      90 * 24 * 60 * 60, // 3 months
      30 * 24 * 60 * 60, // 1 month
      7 * 24 * 60 * 60, // 1 week
      24 * 60 * 60 // 1 day
    ]

    const windowSpan = endTime - startTime

    for (const gapInSeconds of gaps) {
      // Skip any gap that spans the whole requested window. Its first batch would just
      // re-request the exact window that already came back as FILE (which is what triggered
      // this fallback), producing a duplicate API call that returns no new data.
      if (gapInSeconds >= windowSpan) {
        console.log(
          `[SimpleSmartSync] Skipping ${
            gapInSeconds / (24 * 60 * 60)
          } day gap (>= window span, would duplicate triggering call)`
        )
        continue
      }

      console.log(`[SimpleSmartSync] Trying with ${gapInSeconds / (24 * 60 * 60)} day gap...`)

      const result = await this.fetchDataWithGap(
        entityId,
        entityType,
        startTime,
        endTime,
        gapInSeconds,
        orderBy
      )

      if (result.length > 0) {
        console.log(`[SimpleSmartSync] Found data with ${gapInSeconds / (24 * 60 * 60)} day gap`)
        return result
      }
    }

    console.log('[SimpleSmartSync] No data found with any gap size')
    return []
  }

  private async fetchDataWithGap(
    entityId: string,
    entityType: ENTITY_TYPE,
    startTime: number,
    endTime: number,
    gapInSeconds: number,
    orderBy: number
  ): Promise<any[]> {
    const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
    const ONE_DAY = 24 * 60 * 60
    let allTransactions: any[] = []
    let currentEndTime = endTime
    let batchCount = 0
    const maxBatches = 100

    console.log(`[SimpleSmartSync] Fetching with gap: ${gapInSeconds / ONE_DAY} days`)

    // Walk [startTime, endTime] backwards in steps of `gapInSeconds`. Clamp the start of each
    // batch to `startTime` so the final partial window is still fetched (the loop terminates
    // when currentEndTime reaches startTime).
    while (batchCount < maxBatches && currentEndTime > startTime) {
      batchCount++

      const currentStartTime = Math.max(currentEndTime - gapInSeconds, startTime)
      const timeRange = { start_time: currentStartTime, end_time: currentEndTime }

      console.log(`[SimpleSmartSync] Gap batch ${batchCount}:`, timeRange)

      const response = await getAccountTransactionsApi({
        id: entityId,
        start_time: timeRange.start_time,
        end_time: timeRange.end_time,
        orderBy,
        role: isCustomer ? ROLE.CUSTOMER : ROLE.SUPPLIER,
        is_asc_order: true
      })

      if (response.type === API_RESPONSE_TYPE.SUCCESS && response.data?.list_data?.transactions) {
        const transactions = response.data.list_data.transactions
        if (transactions.length > 0) {
          console.log(
            `[SimpleSmartSync] Gap batch ${batchCount} found ${transactions.length} transactions`
          )
          allTransactions = [...allTransactions, ...transactions]
        } else {
          console.log(`[SimpleSmartSync] Gap batch ${batchCount} returned empty transactions`)
          break // No more data, stop fetching
        }
      } else if (
        response.type === API_RESPONSE_TYPE.SUCCESS &&
        (response.data?.list_data === null || response.data?.list_data === undefined)
      ) {
        // FILE response: this window has too many transactions to inline. Preferred
        // fix: decode the exported file directly (no data loss). If the decoder is
        // disabled/unsupported or decoding fails, fall back to subdividing the window
        // with a smaller gap and recursing until it fits or we hit the 1-day floor
        // (at which point <=1 day windows are still silently skipped — the legacy bug).
        let handledByDecode = false
        if (response.data?.file_data) {
          this.fileResponseCount++
          const decoded = await this.tryDecodeFileData(response.data.file_data)
          if (decoded) {
            console.log(
              `[SimpleSmartSync] Gap batch ${batchCount} decoded ${decoded.length} txns from FILE`
            )
            allTransactions = [...allTransactions, ...decoded]
            handledByDecode = true
          }
        }

        if (!handledByDecode) {
          const span = currentEndTime - currentStartTime
          if (span <= ONE_DAY) {
            console.warn(
              `[SimpleSmartSync] Segment ${currentStartTime}-${currentEndTime} still returns FILE at <=1 day granularity; cannot subdivide further without a file download. Skipping this window.`
            )
          } else {
            const smallerGap = Math.max(Math.floor(span / 4), ONE_DAY)
            console.log(
              `[SimpleSmartSync] Gap batch ${batchCount} returned FILE, subdividing ${
                span / ONE_DAY
              }d window with ${smallerGap / ONE_DAY}d gap`
            )
            const subTransactions = await this.fetchDataWithGap(
              entityId,
              entityType,
              currentStartTime,
              currentEndTime,
              smallerGap,
              orderBy
            )
            allTransactions = [...allTransactions, ...subTransactions]
          }
        }
      } else {
        console.log(`[SimpleSmartSync] Gap batch ${batchCount} failed`)
        break // Stop on error
      }

      // Move to the next (older) batch
      currentEndTime = currentStartTime
    }

    console.log(
      `[SimpleSmartSync] Gap fetch completed: ${allTransactions.length} total transactions`
    )
    return allTransactions
  }

  /**
   * Decode a FILE-response `file_data` payload (signed URL + AES key) using the
   * lazy-loaded shared decoder. Returns null (→ caller falls back to subdivide)
   * when the decoder is unsupported, the payload is incomplete, or the decode
   * fails. Never throws.
   */
  private async tryDecodeFileData(fileData: any): Promise<any[] | null> {
    try {
      const url = fileData?.file
      const encryptionKey = fileData?.encryption_key
      if (!url || !encryptionKey) {
        return null
      }
      const decoderMod = await import(/* webpackChunkName: "txn-seed" */ './decoder')
      if (!decoderMod.isDecoderSupported()) {
        return null
      }
      return await decoderMod.downloadAndDecodeTransactionFile({
        url,
        encryption_key: encryptionKey
      })
    } catch (err) {
      console.warn('[SimpleSmartSync] FILE decode failed, falling back to subdivide:', err)
      return null
    }
  }

  async clearCache(entityId: string, entityType: ENTITY_TYPE): Promise<void> {
    // Use the singleton, not `this.dbManager` — the latter is null until a ledger
    // sync has run (a login → seed flow may never initialize it).
    const dbManager = getTransactionDatabaseManager()
    await dbManager.init()
    await dbManager.deleteTransactionsByEntity(entityId, entityType)
    await dbManager.clearMetadata(entityId, entityType)
  }

  async clearAllCache(): Promise<void> {
    // IMPORTANT: use the singleton directly. `this.dbManager` is only set by
    // initialize() (i.e. after a ledger sync); on a login → seed → logout with no
    // ledger opened it is still null, and the old `this.dbManager.clearAllData()`
    // threw a TypeError that signout silently swallowed — so IndexedDB was NEVER
    // cleared. `clearAllData` opens the DB itself if needed.
    const dbManager = getTransactionDatabaseManager()
    await dbManager.clearAllData()
  }
}

// Singleton instance
let simpleSmartSyncInstance: SimpleSmartSync | null = null

export const getSimpleSmartSync = (): SimpleSmartSync => {
  if (!simpleSmartSyncInstance) {
    simpleSmartSyncInstance = new SimpleSmartSync()
  }
  return simpleSmartSyncInstance
}

export default SimpleSmartSync
